import { Link } from 'react-router-dom'
import { ArrowLeft, Shield } from 'lucide-react'
import { useTranslation } from '../i18n/LanguageContext'

export function PrivacyPolicy() {
  const { language } = useTranslation()

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Back Button */}
            <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </Link>

            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-purple-500 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-gray-900">KeyCore AI</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          {language === 'en' ? (
            // English Version
            <>
              <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
              <p className="text-gray-600 mb-6">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Welcome to KeyCore AI Studio ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered multimodal content creation platform.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

                <h3 className="text-xl font-medium text-gray-900 mb-3">2.1 Information You Provide</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Account information (email, username, password)</li>
                  <li>Profile information (full name, avatar)</li>
                  <li>Content you create using our platform</li>
                  <li>Communications with our support team</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-900 mb-3">2.2 Information We Collect Automatically</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Usage data and analytics</li>
                  <li>Device information and browser data</li>
                  <li>IP addresses and location data</li>
                  <li>Cookies and similar technologies</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Provide and maintain our AI services</li>
                  <li>Process and fulfill your requests</li>
                  <li>Improve and optimize our platform</li>
                  <li>Communicate with you about our services</li>
                  <li>Ensure platform security and prevent abuse</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy:
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>With service providers who assist our operations</li>
                  <li>To comply with legal obligations</li>
                  <li>To protect our rights and prevent fraud</li>
                  <li>In connection with a business transfer</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. AI Content and Data Processing</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our platform uses advanced AI technologies to process your content:
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Text prompts are processed by AI models to generate images and audio</li>
                  <li>Generated content is stored securely in your account</li>
                  <li>We retain minimal metadata for service improvement</li>
                  <li>You maintain ownership of your created content</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Security</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We implement appropriate technical and organizational measures to protect your personal information:
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments</li>
                  <li>Access controls and authentication</li>
                  <li>Regular backups and disaster recovery</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Depending on your location, you may have the following rights regarding your personal information:
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Access to your personal information</li>
                  <li>Correction of inaccurate information</li>
                  <li>Deletion of your personal information</li>
                  <li>Restriction or objection to processing</li>
                  <li>Data portability</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Cookies and Tracking</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use cookies and similar technologies to enhance your experience and analyze usage patterns. You can control cookie preferences through your browser settings.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to This Policy</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy or our data practices, please contact us at:
                </p>
                <p className="text-gray-700">
                  Email: privacy@keycore.tech<br />
                  Address: KeyCore AI Studio Privacy Team
                </p>
              </section>
            </>
          ) : (
            // Chinese Version
            <>
              <h1 className="text-4xl font-bold text-gray-900 mb-8">隐私政策</h1>
              <p className="text-gray-600 mb-6">最后更新: {new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. 引言</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  欢迎使用 KeyCore AI Studio（以下简称"我们"、"我们的"或"本平台"）。我们致力于保护您的隐私和个人信息安全。本隐私政策解释了当您使用我们的AI多模态内容创作平台时，我们如何收集、使用、披露和保护您的信息。
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. 我们收集的信息</h2>

                <h3 className="text-xl font-medium text-gray-900 mb-3">2.1 您提供的信息</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>账户信息（邮箱、用户名、密码）</li>
                  <li>个人资料信息（全名、头像）</li>
                  <li>您使用平台创建的内容</li>
                  <li>与我们客服团队的沟通记录</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-900 mb-3">2.2 我们自动收集的信息</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>使用数据和分析信息</li>
                  <li>设备信息和浏览器数据</li>
                  <li>IP地址和地理位置信息</li>
                  <li>Cookies和类似技术</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. 我们如何使用您的信息</h2>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>提供和维护我们的AI服务</li>
                  <li>处理和完成您的请求</li>
                  <li>改进和优化我们的平台</li>
                  <li>与您沟通我们的服务相关事宜</li>
                  <li>确保平台安全并防止滥用</li>
                  <li>遵守法律义务</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. 信息共享和披露</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  除非获得您的同意，否则我们不会出售、交易或以其他方式将您的个人信息转让给第三方，但以下情况除外：
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>与协助我们运营的服务提供商共享</li>
                  <li>为了遵守法律义务</li>
                  <li>为了保护我们的权利和防止欺诈</li>
                  <li>在业务转让过程中</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. AI内容和数据处理</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  我们的平台使用先进的AI技术来处理您的内容：
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>文本提示由AI模型处理以生成图像和音频</li>
                  <li>生成的内容安全存储在您的账户中</li>
                  <li>我们仅保留最少的元数据用于服务改进</li>
                  <li>您保留对创建内容的拥有权</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. 数据安全</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  我们实施适当的技术和组织措施来保护您的个人信息：
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>传输中和静态数据的加密</li>
                  <li>定期安全评估</li>
                  <li>访问控制和身份验证</li>
                  <li>定期备份和灾难恢复</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. 您的权利</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  根据您所在的位置，您可能对个人信息享有以下权利：
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>访问您的个人信息</li>
                  <li>更正不准确的信息</li>
                  <li>删除您的个人信息</li>
                  <li>限制或反对处理</li>
                  <li>数据可移植性</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Cookies和跟踪</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  我们使用Cookies和类似技术来增强您的体验并分析使用模式。您可以通过浏览器设置控制Cookie偏好。
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. 儿童隐私</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  我们的服务不适用于13岁以下儿童。我们不会 knowingly 收集13岁以下儿童的个人信息。
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. 本政策的变更</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  我们可能会不时更新本隐私政策。我们将在本页面发布新政策并更新"最后更新"日期来通知您任何重大变更。
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. 联系我们</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  如果您对本隐私政策或我们的数据实践有任何疑问，请通过以下方式联系我们：
                </p>
                <p className="text-gray-700">
                  邮箱: privacy@keycore.tech<br />
                  地址: KeyCore AI Studio 隐私团队
                </p>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
